import * as React from 'react';
import ReactDOM from 'react-dom';

import Toast, { type ToastVariant } from 'components/atoms/Toast';
import { runAfterDomLoad } from 'libs/runAfterDomLoad';

const FAVORITE_TOAST_EVENT = 'tanp:favorites:show-toast';

type FavoriteToastEventDetail = {
  isPublished: boolean;
  favorited: boolean;
};

type ToastState = {
  visible: boolean;
  variant: ToastVariant;
  message: string;
  displayId: number;
};

const TOAST_MESSAGES = {
  wishlist: {
    add: 'ウィッシュリストに追加しました',
    remove: 'ウィッシュリストから削除しました',
  },
  mylist: {
    add: 'マイリストに追加しました',
    remove: 'マイリストから削除しました',
  },
};

const buildToastMessage = (detail: FavoriteToastEventDetail): string => {
  const listType = detail.isPublished ? 'wishlist' : 'mylist';
  const mode = detail.favorited ? 'add' : 'remove';
  return TOAST_MESSAGES[listType][mode];
};

const FavoriteToastApp: React.FC = () => {
  const [toastState, setToastState] = React.useState<ToastState>({
    visible: false,
    variant: 'success',
    message: '',
    displayId: 0,
  });

  const hideToast = React.useCallback(() => {
    setToastState((prev) => ({
      ...prev,
      visible: false,
    }));
  }, []);

  React.useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<FavoriteToastEventDetail>;
      const detail = customEvent.detail;
      if (!detail) {
        return;
      }
      setToastState((prev) => ({
        visible: true,
        variant: 'success',
        message: buildToastMessage(detail),
        displayId: prev.displayId + 1,
      }));
    };

    window.addEventListener(FAVORITE_TOAST_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(FAVORITE_TOAST_EVENT, handler as EventListener);
    };
  }, []);

  return (
    <Toast
      key={toastState.displayId}
      visible={toastState.visible}
      variant={toastState.variant}
      message={toastState.message}
      onAutoHide={hideToast}
    />
  );
};

const mountFavoriteToast = () => {
  const existingNode = document.getElementById('favorite-toast-root');
  if (existingNode) {
    ReactDOM.render(<FavoriteToastApp />, existingNode);
    return;
  }

  const mountNode = document.createElement('div');
  mountNode.id = 'favorite-toast-root';
  document.body.appendChild(mountNode);
  ReactDOM.render(<FavoriteToastApp />, mountNode);
};

runAfterDomLoad(() => {
  mountFavoriteToast();
});
